import { Injectable, inject } from '@angular/core';
import { CanActivateChildFn, RedirectCommand, Router } from '@angular/router';

/**
 * The scanned table code (the ?table= from the QR link) and what has been done with it.
 *
 * Trello 4566: the guest app used to drop ?table= on its first redirect, so a later load
 * of the current URL in another storage context (reload, "Open in Safari" from an in-app
 * browser, a second tab) signed in a new anonymous user without a table.
 *
 * Because the table now stays in the URL, the app must also remember that the server has
 * already been told about it: otherwise every reload would count as a new scan (the server
 * re-derives the deliver type, refreshes the scan time and can de-current the cart). That
 * memory lives in sessionStorage, so it follows the tab: a reload or back-forward in the same
 * tab finds it, a fresh tab, an incognito tab or an in-app-browser handoff does not and sends
 * the table again, which is the case the fix exists for.
 */
@Injectable({ providedIn: 'root' })
export class TableCodeService {
  /** Last table code seen in the URL. The router puts it back whenever a navigation drops it. */
  urlCode: string | null = null;
  /** The Firebase user the app currently acts as; AuthService sets it whenever the user changes. */
  uid: string | null = null;

  private static readonly STORAGE_KEY = 'heynow.table.applied';

  /** The venue did not know the code: stop putting it back into the URL. */
  forget(): void {
    this.urlCode = null;
  }

  /**
   * The server answers a code the venue does not know with a plain 500 and the message "Wrong code" (the API's
   * catch-all handler). The message is the only thing that tells it apart from a real server error.
   */
  static isWrongCode(error: any): boolean {
    return typeof error?.error?.message === 'string' && error.error.message.includes('Wrong code');
  }
  private applied: Applied | null | undefined;

  /** True when the server has already applied this table code for the current user, in this tab. */
  isApplied(code: string | null | undefined): boolean {
    if (!code || !this.uid) return false;
    const a = this.read();
    return a != null && a.code === code && a.uid === this.uid;
  }

  /** Call only once the server has confirmed the table. One record (code + user), one write. */
  markApplied(code: string | null | undefined): void {
    if (!code || !this.uid) return;
    this.applied = { code, uid: this.uid };
    try {
      sessionStorage.setItem(TableCodeService.STORAGE_KEY, JSON.stringify(this.applied));
    } catch {
      // Storage blocked (private mode, quota): the in-memory copy still covers this page's lifetime.
    }
  }

  private read(): Applied | null {
    if (this.applied !== undefined) return this.applied;
    try {
      const raw = sessionStorage.getItem(TableCodeService.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      this.applied = parsed && typeof parsed.code === 'string' && typeof parsed.uid === 'string' ? parsed : null;
    } catch {
      this.applied = null;
    }
    return this.applied ?? null;
  }
}

interface Applied {
  code: string;
  uid: string;
}

/** Keeps ?table= on every navigation under a venue, so the current URL always carries the scanned table. */
export const keepTableInUrl: CanActivateChildFn = (childRoute, state) => {
  const tables = inject(TableCodeService);
  const inUrl = childRoute.queryParamMap.get('table');
  if (inUrl != null) {
    tables.urlCode = inUrl;
    return true;
  }
  // A URL that carries its own sign-in token (sec + uid) was built by the server and must stay byte-identical:
  // the paystatus page the app opens while Swish or Vipps is launched has to match the return address exactly,
  // or Safari brings the guest back in a new tab. The token identifies the guest, who already has the table.
  if (tables.urlCode == null || childRoute.queryParamMap.has('sec')) {
    return true;
  }
  const router = inject(Router);
  const extras = router.getCurrentNavigation()?.extras ?? {};
  // The address bar is not going to change, so there is nothing to put the table into.
  if (extras.skipLocationChange) {
    return true;
  }
  const tree = router.parseUrl(state.url);
  tree.queryParams = { ...tree.queryParams, table: tables.urlCode };
  // The redirect stands in for the navigation it replaces, so it must leave history the way that navigation
  // would have: a plain navigation pushes an entry (the back button then walks the pages), one made with
  // replaceUrl replaces it. Forcing replaceUrl here replaced the page the guest came from, on every tap, and the
  // back button left the app.
  return new RedirectCommand(tree, { replaceUrl: extras.replaceUrl ?? false, state: extras.state });
};
